'use client'

/**
 * SearchSuggestPanel — the grouped suggestions dropdown for SearchSuggest.
 * Split out of SearchSuggest.tsx (perf budget, 2026-11-04) so hook-only
 * consumers (V3ChromeSearch in the site chrome, HomeHeroSearch) never ship
 * cmdk to every page; only panel consumers (SearchFilters) pay for it.
 */

import { SearchCommand } from '@/app/search/_v3/SearchCommand'
import { SUGGEST_GROUP_LABELS, type SuggestItem } from '@/components/search/SearchSuggest'

type PanelProps = {
  items: SuggestItem[]
  loading?: boolean
  /** Non-null once a fetch has resolved — drives the "No results" state. */
  hasResult?: boolean
  highlight?: number
  idPrefix: string
  /** Return handling is the consumer's job (navigate or apply filters). */
  onPick: (item: SuggestItem) => void
  className?: string
}

/**
 * The grouped dropdown body. Options are anchors (real hrefs — middle-click
 * and copy-link work) whose default navigation is deferred to `onPick` via
 * onMouseDown, so filter-bar consumers can apply filters in place and blur
 * handlers never race the click.
 */
export function SearchSuggestPanel({
  items,
  loading = false,
  hasResult = false,
  highlight = -1,
  idPrefix,
  onPick,
  className,
}: PanelProps) {
  if (loading && items.length === 0) {
    return (
      <div className={className}>
        <p className="px-4 py-2.5 text-sm text-muted-foreground">Searching…</p>
      </div>
    )
  }
  if (hasResult && items.length === 0) {
    return (
      <div className={className}>
        <p className="px-4 py-2.5 text-sm text-muted-foreground">No results</p>
      </div>
    )
  }
  if (items.length === 0) return null

  return (
    <SearchCommand
      className={className}
      items={items.map((item) => ({
        id: item.href,
        group: SUGGEST_GROUP_LABELS[item.kind],
        label: item.label,
        sublabel: item.sublabel,
      }))}
      highlight={highlight}
      idPrefix={idPrefix}
      onPick={(id) => {
        const item = items.find((row) => row.href === id)
        if (item) onPick(item)
      }}
    />
  )
}
